import { useState, useCallback } from 'react';
import type {
  ReactElement,
} from 'react';

import { Layout } from '../Layout';

import {
  MENU_COLLAPSED,
  OPENED_GROUPS,
} from './constants';

import { parseLayoutFromLocalStorage } from './parseLayoutFromLocalStorage';

import type {
  LayoutState,
  LocalStorageLayoutProps,
} from './types';

export function LocalStorageLayout(props: LocalStorageLayoutProps): ReactElement {
  const {
    menuCollapsed: menuCollapsedProp = false,
    openedGroups: openedGroupsProp = undefined,

    onChangeState = undefined,
  } = props;

  const [parseResult] = useState<LayoutState>(() => parseLayoutFromLocalStorage());

  const [menuCollapsed, setMenuCollapsed] = useState<boolean>(() => {
    if (parseResult.menuCollapsed === null) {
      return menuCollapsedProp;
    }

    return parseResult.menuCollapsed;
  });

  const [openedGroups, setOpenedGroups] = useState<{
    [key: string]: boolean;
  }>(() => {
    if (parseResult.openedGroups === null) {
      return openedGroupsProp || {};
    }

    return parseResult.openedGroups;
  });

  const onCollapseMenu = useCallback((nextMenuCollapsed: boolean): void => {
    try {
      localStorage[MENU_COLLAPSED] = JSON.stringify(nextMenuCollapsed);
      // eslint-disable-next-line no-empty
    } catch (e) {}

    setMenuCollapsed(nextMenuCollapsed);

    if (onChangeState) {
      onChangeState({
        menuCollapsed: nextMenuCollapsed,
        openedGroups,
      });
    }
  }, [
    openedGroups,
    onChangeState,
  ]);

  const onToggleGroup = useCallback((groupId: string, value: boolean): void => {
    const nextOpenedGroups = {
      ...openedGroups,
      [groupId]: value,
    };

    setOpenedGroups(nextOpenedGroups);

    try {
      localStorage[OPENED_GROUPS] = JSON.stringify(nextOpenedGroups);
      // eslint-disable-next-line no-empty
    } catch (e) {}

    if (onChangeState) {
      onChangeState({
        menuCollapsed,
        openedGroups: nextOpenedGroups,
      });
    }
  }, [
    menuCollapsed,
    openedGroups,
    onChangeState,
  ]);

  return (
    <Layout
      {...props}
      menuCollapsed={menuCollapsed}
      openedGroups={openedGroups}
      onCollapseMenu={onCollapseMenu}
      onToggleGroup={onToggleGroup}
    />
  );
}
